import { useMemo } from "react";

import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";

import { DropdownOptionResponse, settingsApi } from "@/api";
import { DropdownCategoryMaxLength } from "@/common/constants/dropdownCategoryMaxLength";
import { nameof } from "@/common/helper/commonHelper";
import { dropdownAdminQueryKey, dropdownQueryKey, fetchAllDropdownOptions } from "@/common/hooks/useDropdownOptions";
import { DropdownsFormValues } from "@/pages/system-settings/tabs/dropdowns/DropdownsSettings.types";
import { useSortedCategories } from "@/pages/system-settings/tabs/dropdowns/hooks/useSortedCategories";
import { useTranslation } from "@/translations";

export function useDropdownsSettings() {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();

  const { selectedCategory } = useSortedCategories();

  const { data: allResponses = [] } = useQuery<DropdownOptionResponse[]>({
    queryKey: dropdownAdminQueryKey,
    queryFn: fetchAllDropdownOptions,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const initialValues = useMemo<DropdownsFormValues>(
    () => ({
      category: selectedCategory,
      rows: allResponses.filter((o) => o.category === selectedCategory),
    }),
    [selectedCategory, allResponses]
  );

  const maxValueLength = DropdownCategoryMaxLength[selectedCategory];

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        rows: Yup.array().of(
          Yup.object().shape({
            value: Yup.string().max(maxValueLength, ({ max }) => t("common.validation.maxLength", { max })),
          })
        ),
      }),
    [maxValueLength, t]
  );

  const saveMutation = useMutation({
    mutationFn: (values: DropdownsFormValues) =>
      settingsApi.updateDropdownOptions({
        category: values.category,
        updateDropdownOptionsRequest: {
          options: values.rows
            .filter((r) => r.value.trim().length > 0)
            .map((r) => ({
              id: r.id,
              value: r.value.trim(),
            })),
        },
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: dropdownQueryKey }),
        queryClient.invalidateQueries({ queryKey: dropdownAdminQueryKey }),
      ]);
      showSuccess(t("settings.dropdowns.saveSuccess"));
    },
    onError: () => {
      showError(t("settings.dropdowns.saveError"));
    },
  });

  const formik = useFormikWithDirtyForm<DropdownsFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: (values) => saveMutation.mutateAsync(values),
  });

  const handleAddRow = () => {
    void formik.setFieldValue(nameof<DropdownsFormValues>("rows"), [...formik.values.rows, { id: null, value: "" }]);
  };

  const handleDeleteRow = (index: number) => {
    void formik.setFieldValue(
      nameof<DropdownsFormValues>("rows"),
      formik.values.rows.filter((_, i) => i !== index)
    );
  };

  return {
    formik,
    selectedCategory,
    maxValueLength,
    isSaving: saveMutation.isPending,
    handleAddRow,
    handleDeleteRow,
  };
}
